'use client';

import * as React from 'react';
import { useId, useState } from 'react';
import { PopoverContext } from './Popover.context';
import type { PopoverContextType } from './Popover.types';
import { PopoverContent } from './PopoverContent';
import { PopoverTrigger } from './PopoverTrigger';
import { usePopoverInteractions } from './usePopoverInteractions';
export { usePopoverContext } from './Popover.context';

function Popover({
  children,
  open: controlledOpen,
  onOpenChange,
  hoverDelay = { open: 0, close: 100 },
  initialFocus = -1,
  role: roleProp = 'dialog',
  modal = false,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hoverDelay?: { open: number; close: number };
  initialFocus?: any;
  role?: React.AriaRole;
  modal?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : isOpen;

  const handleOpenChange = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setIsOpen(value);
      }
    },
    [isControlled, onOpenChange]
  );

  const id = useId();

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    arrowRef,
    middlewareData,
    placement,
  } = usePopoverInteractions({
    open,
    onOpenChange: handleOpenChange,
    hoverDelay,
    role: roleProp,
  });

  const value: PopoverContextType = React.useMemo(
    () => ({
      open,
      onOpenChange: handleOpenChange,
      refs,
      floatingStyles,
      context,
      getReferenceProps,
      getFloatingProps,
      id,
      roleProp,
      modal,
      initialFocus,
      arrowRef,
      middlewareData,
      placement,
    }),
    [
      open,
      handleOpenChange,
      refs,
      floatingStyles,
      context,
      getReferenceProps,
      getFloatingProps,
      id,
      roleProp,
      modal,
      initialFocus,
      arrowRef,
      middlewareData,
      placement,
    ]
  );

  return (
    <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>
  );
}

Popover.Trigger = PopoverTrigger as any;
Popover.Content = PopoverContent as any;

export { Popover };
